/**
 * A simple wrapper over the `esri.Map` class.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * map | The `esri.Map` associated with this view. This property is only available after the `render` operation.
 * currentPoint | The `Point` indicating the current point of the cursor on the map.
 * edit | If editing is allowed, the `esri.toolbars.Edit` associated with the map.
 * draw | The `esri.toolbars.Draw` associated with the map.
 * scalebar | The `esri.dijit.Scalebar` associated with the map, if any.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * mapload | The `esri.Map` instance. | Fires when the underlying map finishes initializing.
 * models-selected | The precipitating event, the label, the associated map point, and the set of data models selected | Fired whenever a model or set of models is selected
 * @module app/views/map/MapView
 */
import Backbone from "backbone";
import _ from "underscore";
import MapPointView from "app/views/map/MapPointView";
import SymbolFactory from "app/models/map/SymbolFactory";
import $ from "jquery";
import async from "async";
import "arcgis-js-api/css/esri.css";
import "../../../../styles/specific/esri-override.scss";
import GeoUtils from "app/utils/GeoUtils";
import cms from "core/context-menu-service";
import isMobile from "core/is-mobile";
import modernizr from "modernizr";
import mapContext from "app/views/map/MapContext";
import _remove from "lodash.remove";

//todo improve error handling here
/**
 * A simple wrapper over the `esri.Map` class.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * map | The `esri.Map` associated with this view. This property is only available after the `render` operation.
 * currentPoint | The `Point` indicating the current point of the cursor on the map.
 * edit | If editing is allowed, the `esri.toolbars.Edit` associated with the map.
 * draw | The `esri.toolbars.Draw` associated with the map.
 * scalebar | The `esri.dijit.Scalebar` associated with the map, if any.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * mapload | The `esri.Map` instance. | Fires when the underlying map finishes initializing.
 * @module app/views/map/MapView
 * @extends Backbone.View
 * @class MapView
 */
export default Backbone.View.extend(
  {
    className: "map-search-wrapper map-view",

    attributes: {
      "data-role": "page",
    },

    template: _.template(
      '<div class="header-fill visible-xs"></div><div class="location-map"></div>'
    ),

    events: {
      mouseover: function () {
        if (this.map) {
          this.map.reposition();
        }
      },
    },

    currentPoint: null,

    /**
     * Initializes the view.
     * @param  {object} options The options hash.
     * @param {string} options.basemap The initial base map for the `esri.Map`. See https://developers.arcgis.com/javascript/jsapi/map-amd.html#map1 for more information.
     * @param {Array} options.center An array where the first element is a longitude and the second element is a latitude specifying the initial point on which the map shall be centered.
     * @param {Boolean} options.edit True if editing shall be supported by this map, otherwise false.
     * @param {Boolean|Object} options.scalebar `false` to disable scalebar. `true` to enable scalebar, or object to specify scalebar options. See https://developers.arcgis.com/javascript/jsapi/scalebar-amd.html for possible options. The default value of this parameter is `true`.
     * @param options.context The map context associated with this view. Either this or `options.configuration` should be supplied.
     * @param {app/models/Layout} options.configuration The layer configuration to assign to this view. Either this or `options.context` should be supplied.
     */
    initialize: function (options) {
      this.options = _.defaults({}, options, {
        basemap: "streets",
        center: [41.876276, -87.650371],
        scalebar: true,
      });
      this.currentPoint = [41.876276, -87.650371];
      this._basemap = this.options.basemap;
      this._center = this.options.center;
      this._layers = [];
      this._scalebar = null;

      this._editingLabel = false;

      this.listenTo(
        mapContext.operations,
        "clear-selection",
        function () {
          this.deactivateEdit();
        },
        this
      );

      this.listenTo(
        mapContext.operations,
        "commit-drawing",
        function () {
          this.deactivateDraw(true);
        },
        this
      );

      this.listenTo(
        mapContext.operations,
        "cancel-drawing",
        function () {
          this.deactivateDraw();
        },
        this
      );

      this.listenTo(
        mapContext.operations,
        "highlight-geometry",
        function (model) {
          // assume that model is a FeatureResults. Also, remember typed languages?

          try {
            if (model.length == 1) {
              var editModel = model.first(),
                layer = editModel.originalLayer;

              if (layer.isEditable()) {
                var graphic = layer.toGraphic(editModel);

                if (graphic) {
                  this.activateEdit(graphic, layer);
                }
              }
            }
          } catch (err) {
            console.error(err);
            _errs.push(err);
            // we don't really need to do much with this, just making sure one of our references being null doesn't hurt everything
          }
        },
        this
      );

      this.listenTo(
        mapContext.operations,
        "revert-location-geometry",
        function () {
          var Edit = window.require("esri/toolbars/edit");
          this.edit.deactivate();
          this.edit.activate(
            Edit.MOVE | Edit.EDIT_VERTICES | Edit.SCALE | Edit.ROTATE,
            this._editingLabel
          );
        },
        this
      );

      /*
       * Listen to ourselves so that if we are removed before mapload, we don't set a configuration anyway and handle
       * events with an invalid map
       */
      this.listenTo(this, "mapload", function () {
        var layerConfig =
          this.options.configuration || mapContext.getConfiguration();

        this.setConfiguration(layerConfig);
      });

      Object.defineProperty(this, "scalebar", {
        get: _.bind(function () {
          return this._scalebar;
        }, this),
      });
    },

    /**
     * The Map associated with this MapView.
     * @type Map
     */
    map: null,

    /**
     * Handler for InternalMapService#event:layer-moved
     * @private
     */
    _onServiceLayerMoved: function () {
      this.reorderLayers(this.getConfiguration());
    },

    /**
     * Handler for InternalMapService#event:layer-added
     * @private
     */
    _onServiceLayerAdded: function (service, layer) {
      this._addLayer(service, null, layer);
      this.reorderLayers(this.getConfiguration());
    },

    /**
     * Handler for InternalMapService#event:layer-removed
     * @private
     */
    _onServiceLayerRemoved: function (service, layer) {
      this._removeLayer(layer);
    },

    /**
     * Handler for ObjectLayer#event:child-moved
     * @private
     */
    _onLayerChildMoved: function () {
      this.reorderLayers(this.getConfiguration());
    },

    /**
     * Handler for ObjectLayer#event:child-added
     * @private
     */
    _onLayerChildAdded: function (parent, layer) {
      this._addLayer(parent.serviceLayer, parent, layer);
      this.reorderLayers(this.getConfiguration());
    },

    /**
     * Handler for ObjectLayer#event:child-removed
     * @private
     */
    _onLayerChildRemoved: function (parent, layer) {
      this._removeLayer(layer);
    },

    /**
     * Handler for EditableLayerBase#event:model-removed
     * @private
     */
    _onLayerModelRemoved: function () {
      this.deactivateEdit(true);
      if (this._editingLabel && this._editingLayer) {
        this.map.graphics.remove(this._editingLabel);
        this._editingLayer.layer.remove(this._editingLabel);
        this._editingLabel = false;
        this._editingLayer = false;
      }
    },

    /**
     * Handler for MapLayerBase#event:visibility-changed
     * @param {MapLayerBase} layer The layer on which visibility changed.
     * @private
     */
    _onLayerVisibilityChanged: function (layer) {
      if (this._editingLayer == layer) {
        this.deactivateEdit(true);
      }
    },

    /**
     * Handler for ObjectLayer#event:name-field-changed
     * @param {ObjectLayer} layer The layer experiencing the change
     * @param {String} nameFieldValue The new value of the name field
     * @private
     */
    _onLayerNameFieldChanged: function (layer) {
      var displayNameField = layer.getDisplayNameField();
      var SimpleRenderer = window.require("esri/renderers/SimpleRenderer");

      this._labelLayer.removeFeatureLayer(layer.layer.id);

      if (displayNameField) {
        this._labelLayer.addFeatureLayer(
          layer.layer,
          new SimpleRenderer(this._createTextSymbol()),
          "{" + displayNameField.id + "}"
        );
      }
    },

    /**
     * Handler for Layout#event:service-added
     * @private
     */
    _onServiceAdded: function (service) {
      this._addService(service);
      this.reorderLayers(this.getConfiguration());
    },

    /**
     * Handler for Layout#event:service-removed
     * @private
     */
    _onServiceRemoved: function (service) {
      this._removeService(service);
    },

    /**
     * Handler for Layout#event:service-moved
     * @private
     */
    _onServiceMoved: function () {
      this.reorderLayers(this.getConfiguration());
    },

    /**
     * Handler for MapServiceBase#event:visibility-changed
     * @param {MapServiceBase} service The service whose visibility has changed.
     * @param {boolean} visible The current visibility value
     * @private
     */
    _onServiceVisibilityChanged: function (service, visible) {
      if (_.contains(service.layers, this._editingLayer) && !visible) {
        this.deactivateEdit(true);
      }
    },

    /**
     * Attaches event handlers to the given service.
     * @param {MapServiceBase} service The service to which events should be attached.
     * @private
     */
    _attachService: function (service) {
      this.listenTo(service, "layer-moved", this._onServiceLayerMoved);
      this.listenTo(service, "layer-added", this._onServiceLayerAdded);
      this.listenTo(service, "layer-removed", this._onServiceLayerRemoved);
      this.listenTo(
        service,
        "visibility-changed",
        this._onServiceVisibilityChanged
      );
    },

    /**
     * Detaches event handlers from the given service.
     * @param {MapServiceBase} service The service from which events should be detached.
     * @private
     */
    _detachService: function (service) {
      this.stopListening(service);
    },

    /**
     * Attaches event handlers to the given layer.
     * @param {MapLayerBase} layer The layer to which events should be attached.
     * @private
     */
    _attachLayer: function (layer) {
      var self = this;
      var SimpleRenderer = window.require("esri/renderers/SimpleRenderer");
      var displayNameField = layer.getDisplayNameField();

      this._detachLayer(layer);

      this.listenTo(layer, "child-moved", this._onLayerChildMoved);
      this.listenTo(layer, "child-added", this._onLayerChildAdded);
      this.listenTo(layer, "child-removed", this._onLayerChildRemoved);
      this.listenTo(layer, "model-removed", this._onLayerModelRemoved);
      this.listenTo(
        layer,
        "visibility-changed",
        this._onLayerVisibilityChanged
      );
      this.listenTo(layer, "name-field-changed", this._onLayerNameFieldChanged);
      if (layer.layer) {
        layer.layer._mapViewClickHandler = layer.layer.on("click", function (
          evt
        ) {
          //clicking on part of the layer
          self._onLayerClick(layer, evt);
        });

        if (displayNameField) {
          this._labelLayer.addFeatureLayer(
            layer.layer,
            new SimpleRenderer(this._createTextSymbol()),
            "{" + displayNameField.id + "}"
          );
        }
      }
    },

    _createTextSymbol: function () {
      var TextSymbol = window.require("esri/symbols/TextSymbol");
      var Font = window.require("esri/symbols/Font");

      var font = new Font();
      var symbol = new TextSymbol({
        horizontalAlignment: "left",
        yoffset: 5,
        xoffset: 5,
      });

      font.setSize("18px");
      symbol.setFont(font);

      return symbol;
    },

    /**
     * Detaches event handlers from the given layer.
     * @param {MapLayerBase} layer The layer from which events should be detached.
     * @private
     */
    _detachLayer: function (layer) {
      this.stopListening(layer);
      if (layer.layer && layer.layer._mapViewClickHandler) {
        layer.layer._mapViewClickHandler.remove();
        this._labelLayer.removeFeatureLayer(layer.layer.id);
      }
    },

    _onLayerClick: function (layer, evt) {
      if (!this.isMeasuring() && !this.isDrawingActive()) {
        dojo.stopEvent(evt);

        var models = this.findNearbyFeatures(layer, evt.graphic, evt.mapPoint);

        if (models.length) {
          this.trigger(
            "models-selected",
            evt,
            evt.graphic,
            evt.mapPoint,
            models
          );
        }
      }
    },

    /**
     * Searches all layers in all services for features close to the given point. If none are found, returns the model associated with the graphic.
     * @param layer
     * @param graphic
     * @param mapPoint
     * @return {Array} An array of all models found.
     */
    findNearbyFeatures: function (layer, graphic, mapPoint) {
      var models = [];
      var config = mapContext.getConfiguration();

      if (layer.serviceLayer && layer.serviceLayer.service.get("external")) {
        models = layer.findModelsFromLabel(graphic, mapPoint);
      } else {
        models = models.concat(
          config.workingLayer.findModelsFromLabel(graphic, mapPoint)
        );

        _.each(config.serviceLayers, function (serviceLayer) {
          if (serviceLayer.isSelectable() && serviceLayer.isVisible()) {
            serviceLayer.eachLayer(function (layer) {
              if (
                layer.isVisible() &&
                layer.isSelectable() &&
                !layer.hasChildren()
              ) {
                models = models.concat(
                  layer.findModelsFromLabel(graphic, mapPoint)
                );
              }
            });
          }
        });
      }

      return models;
    },

    /**
     * Adds a layer to the map.
     * @param {esri.Layer} layer The layer to add.
     */
    addLayer: function (layer) {
      this._layers.push(layer);

      if (this.map) {
        this.map.addLayer(layer, 0);
      }
    },

    /**
     * Removes a layer from the map.
     * @param  {esri.Layer} layer The layer to remove.
     */
    removeLayer: function (layer) {
      _remove(this._layers, layer);

      if (this.map && layer._div) {
        this.map.removeLayer(layer);
      }
    },

    /**
     * Reorder the layers in the map to match the order specified in the layout, from "top" to "bottom".
     * @param {Layout} layout The layout providing the ordering information.
     */
    reorderLayers: function (layout) {
      var idx = 0;
      var self = this;
      var reorder = function reorder(layer, index) {
        self.map.reorderLayer(layer.getMapLayer(), index);
      };

      reorder(layout.workingLayer, idx++);
      _.each(layout.serviceLayers, function (service) {
        service.eachLayer(function (layer) {
          if (!layer.hasChildren()) {
            reorder(layer, idx++);
          }
        });
      });
    },

    /**
     * Adds a layer model to the "bottom" of the map.
     * @param {MapServiceBase} service The service containing the layer to add. May be null if `index` is specified.
     * @param {MapLayerBase} parent The parent layer of the layer to add. May be null.
     * @param {MapLayerBase} layer The layer to add.
     * @private
     * @return {Promise} Resolves when the layer has loaded or rejects with an error.
     */
    _addLayer: function (service, parent, layer) {
      var self = this;
      var promise;

      if (layer.hasChildren()) {
        var deferred = new $.Deferred();

        promise = deferred.promise();

        async.eachSeries(
          layer.children,
          function (child, cb) {
            var addLayerPromise = self._addLayer(service, layer, child);

            addLayerPromise
              .done(function () {
                self._attachLayer(layer);
                cb();
              })
              .fail(function (err) {
                cb(err);
              });
          },
          function (err) {
            if (err) {
              return deferred.reject(err);
            } else {
              deferred.resolve();
            }
          }
        );
      } else {
        promise = self.attach(layer).then(function (layer) {
          self._attachLayer(layer);
        });
      }

      return promise;
    },

    /**
     * Attaches the layer to this MapView.
     * @param {MapLayerBase} layer The layer to attach.
     *
     * @public
     */
    attach: function (layer) {
      var self = this;

      return layer.attach(this.map).done(function (layer) {
        //Add the layers, after pausing briefly
        setTimeout(function () {
          self.addLayer(layer.layer);
        }, 800);

        if (layer.layer.visible) {
          layer.layer.setVisibility(false);
          layer.layer.setVisibility(true);
        }
      });
    },

    /**
     * Removes the layer from the map.
     * @param {MapLayerBase} layer The layer to remove. Children of the layer will also be removed.
     * @private
     */
    _removeLayer: function (layer) {
      this.deactivateEdit();
      this._detachLayer(layer);

      if (layer.hasChildren()) {
        _.each(
          layer.children,
          function (child) {
            this._removeLayer(child);
          },
          this
        );
      } else {
        this.removeLayer(layer.layer);

        if (layer.layer.labelLayer) {
          this.removeLayer(layer.layer.labelLayer);
        }

        layer.detach(this.map);
      }
    },

    /**
     * Adds a service to the map. All child layers are added to the map, in turn.
     * @param {MapServiceBase} service The service to add.
     * @private
     */
    _addService: function (service) {
      this._attachService(service);

      var self = this;

      var layers = service.layers.slice();

      async.eachSeries(layers, function (child, cb) {
        self
          ._addLayer(service, null, child)
          .done(function () {
            cb();
          })
          .fail(function (err) {
            cb(err);
          });
      });
    },

    /**
     * Removes an entire service from the MapView.
     * @param {MapServiceBase} service The service to remove.
     * @private
     */
    _removeService: function (service) {
      this._detachService(service);

      _.each(
        service.layers,
        function (layer) {
          this._removeLayer(layer);
        },
        this
      );
    },

    /**
     * Engages the draw mode.
     * @param  {string} drawMode One of the constants in esri/toolbars/Draw
     * @param {MapLayerBase} layer The layer on which to activate draw.
     */
    activateDraw: function (drawMode, layer) {
      if (layer.canDraw()) {
        if (this.draw) {
          this.draw.setFillSymbol(layer.getSymbolFactory().fill());
          this.draw.setMarkerSymbol(layer.getSymbolFactory().marker());
          this.draw.setLineSymbol(layer.getSymbolFactory().line());
        }

        this.deactivateEdit(true);
        this._drawMode = drawMode;

        if (this.draw) {
          this.draw.activate(drawMode);
          this.draw.active = true;
          this._drawingLayer = layer;
        }

        this.trigger("activate", drawMode, layer);
      }
    },

    /**
     * Disengages the draw mode.
     * @param {Boolean} [commit] `true` to indicate that the current shape should be closed.
     */
    deactivateDraw: function (commit) {
      if (this._drawingLayer != null) {
        if (this._drawingLayer.canDraw()) {
          if (this.draw) {
            if (commit) {
              this.draw.finishDrawing();
            } else {
              this._drawMode = null;
              this.draw.deactivate();
              this.draw.active = false;
              this._drawingLayer = false;
              this.trigger("deactivate");
            }
          } else {
            this._drawMode = null;
            this._drawingLayer = null;
            this.trigger("deactivate");
          }
        }
      } else {
        if (this.draw) {
          this._drawMode = null;
          this.draw.deactivate();
          this.draw.active = false;
          this._drawingLayer = false;
        } else {
          this._drawMode = null;
          this._drawingLayer = null;
        }
      }
    },

    /**
     * Gets a value indicating whether or not the drawing mode for this layer is activated.
     * @return {Boolean} True if drawing, otherwise false.
     */
    isDrawingActive: function () {
      return this.draw && this.draw.active;
    },

    /**
     * Engages editing mode for a particular esri graphic.
     * @param {esri.Graphic} label The Graphic to edit.
     * @param {MapLayerBase} layer The layer on which editing is taking place.
     */
    activateEdit: function (label, layer) {
      var Edit = window.require("esri/toolbars/edit");
      var graphicLayer = this.getConfiguration().workingLayer.layer;
      var originalLayer = layer.layer;

      if (this._editingLabel != label) {
        this.deactivateEdit(); //make sure any object being edited gets returned to its correct location
      }

      //move the graphic to the working layer so that it can be moved.
      //for some reason moving of graphics doesn't seem to work apart from the default graphics layer
      if (originalLayer != graphicLayer) {
        graphicLayer.add(label);
        originalLayer.remove(label);
        graphicLayer.refresh();
        originalLayer.refresh();
      }

      this.edit.activate(
        Edit.MOVE | Edit.EDIT_VERTICES | Edit.SCALE | Edit.ROTATE,
        label
      );
      this._editingLayer = layer;
      this._editingLabel = label;
      //Check for mobile
      if (isMobile) {
        this.map.disableMapNavigation();
      }
    },

    /**
     * Deactivates the editing state.
     * @param  {Boolean} [cancel:false] True to undo any changes, otherwise they will be committed as-is.
     */
    deactivateEdit: function (cancel) {
      if (this.edit) {
        if (this._editingLabel) {
          var labels = this._editingLayer.getLabels();
          var idx = labels.indexOf(this._editingLabel);
          var model = idx !== -1 ? this._editingLayer.getGeometry(idx) : null;

          if (!cancel && model) {
            model.parseEsriGeometry(this._editingLabel.geometry);
          }

          var graphicLayer = this.getConfiguration().workingLayer.layer;

          //Move the graphic to the original layer
          if (this._editingLayer.layer != graphicLayer) {
            graphicLayer.remove(this._editingLabel);
            this._editingLayer.layer.add(this._editingLabel);
            this._editingLayer.layer.refresh();
          }
          graphicLayer.refresh();

          this._editingLayer.unhighlight(model);
        }

        this.edit.deactivate();
      }
      if (isMobile) {
        this.map.enableMapNavigation();
      }
    },

    /**
     * Gets a value indicating whether or not the editing mode for this layer is activated.
     * @return {Boolean} True if editing, otherwise false.
     */
    isEditing: function () {
      return !!this._editingLabel;
    },

    /**
     * Clears the map layers.
     */
    clearLayers: function () {
      if (this.map) {
        var map = this.map;

        _.each(this._layers, function (layer) {
          map.removeLayer(layer);
        });
      }

      this._layers = [];
    },

    /**
     * Gets the number of layers in the map. This includes both graphical and non-graphical layers.
     * @return {Integer}
     * @private
     */
    _layerCount: function () {
      var count;

      if (this.map) {
        count = this.map.layerIds.length + this.map.graphicsLayerIds.length;
      }

      return count;
    },

    /**
     * Brings the specified layer to the front of the stack.
     * @param  {esri.Layer} layer The layer to reorder.
     */
    bringLayerToFront: function (layer) {
      if (this.map) {
        this.map.reorderLayer(layer, this._layerCount() - 1);
      }
    },

    /**
     * Sends the specified layer to the bottom of the stack.
     * @param  {esri.Layer} layer The layer to reorder.
     */
    sendLayerToBack: function (layer) {
      if (this.map) {
        this.map.reorderLayer(layer, 0);
      }
    },

    /**
     * Gets the set of layers in the map.
     * @return {Array} An array of `esri.Layer` instances.
     */
    getLayers: function () {
      return this._layers.slice();
    },

    /**
     * Gets the current base map.
     * @return {string} The base map.
     */
    getBaseMap: function () {
      return this._basemap;
    },

    /**
     * Sets the base map.
     * @param {string} basemap The identifier of the base map to set.
     */
    setBaseMap: function (basemap) {
      this._basemap = basemap;

      if (this.map) {
        this.map.setBasemap(basemap);
      }
    },

    /**
     * Gets the most recently-set center point. This is not necessarily the current map center.
     * @return {Array} The center point. The first element is the longitude, and the second is the latitude.
     */
    getCenter: function () {
      return this._center;
    },

    /**
     * Sets the center point and instructs the underlying map to center on that point.
     * @param {Array} center The center point. The first element is the longitude, and the second is the latitude.
     */
    setCenter: function (center) {
      //center is [long, lat]
      var MapPoint = window.require("esri/geometry/Point");
      this._center = center;

      if (this.map) {
        this.map.centerAt(new MapPoint(center[0], center[1]));
      }
    },

    /**
     * Gets the configuration associated with this view.
     * @return {Layout} The configuration.
     */
    getConfiguration: function () {
      return this._config;
    },

    /**
     * Sets the configuration associated with this view.
     * @param {Layout} config The configuration.
     */
    setConfiguration: function (config) {
      if (this._config) {
        this._removeLayer(this._config.workingLayer);
        _.each(
          this._config.serviceLayers,
          function (serviceLayer) {
            this._removeService(serviceLayer);
          },
          this
        );

        this.stopListening(this._config);
      }

      this._config = config;

      if (this._config) {
        _.each(this._config.serviceLayers, this._addService, this);
        this._addLayer(null, null, this._config.workingLayer);
        this.reorderLayers(this._config);

        this.listenTo(this._config, "service-removed", this._onServiceRemoved);
        this.listenTo(this._config, "service-added", this._onServiceAdded);
        this.listenTo(this._config, "service-moved", this._onServiceMoved);
      }
    },

    /**
     * Gets and sets a value indicating whether or not the map is in measure mode.
     * @param  {Boolean}  [value] The new value of the property. If not specified, no change is made.
     * @return {Boolean}       True if measurement is taking place, otherwise false.
     */
    isMeasuring: function (value) {
      if (_.isBoolean(value)) {
        if (value) {
          this._isMeasuring = true;
        } else if (!value) {
          this._isMeasuring = false;
        }
      }

      return this._isMeasuring;
    },

    /**
     * Refreshes the underlying geometry model of the feature being edited.
     */
    refreshGeometry: function () {
      //there's something fishy going on with moving graphics.
      //our current solution is to deactivate editing and immediately reactivate it
      //without this bit in place, moving a geometry will end up moving the editing box but not the geometry itself, for some reason.
      var label = this._editingLabel,
        layer = this._editingLayer,
        symbolFactory = SymbolFactory.highlight(),
        symbol = symbolFactory.byType(label.geometry.type);

      this.deactivateEdit();
      this.activateEdit(label, layer);
      label._oldSymbol = label.symbol;
      label.setSymbol(symbol);
    },

    _mapLoad: function () {
      var Edit = window.require("esri/toolbars/edit");
      var self = this;

      this.setBaseMap(this.getBaseMap());

      function cancelEdit() {
        if (!self.isMeasuring() && !self.isDrawingActive()) {
          self.deactivateEdit();
        }
      }

      if (this.options.edit) {
        this.edit = new Edit(this.map);
        this.edit.on("graphic-move", _.bind(this._onGraphicMove, this));
        this.edit.on("vertex-move", _.bind(this._onVertexMove, this));
        this.edit.on(
          "graphic-move-stop",
          _.bind(this._onGraphicMoveStop, this)
        );
        this.edit.on("vertex-move-stop", function () {
          self.refreshGeometry();
          self.getConfiguration().workingLayer.layer.refresh();
        });
        this.edit.on("scale-stop", function () {
          self.refreshGeometry();
          self.getConfiguration().workingLayer.layer.refresh();
        });
      }

      if (this.scalebar) {
        //this.scalebar.startup();
      }

      if (modernizr.touchevents) {
        this.map.hideZoomSlider();
      }

      this.map.on("click", cancelEdit);

      this.trigger("mapload", this.map);
    },

    _onVertexMove: function (evt) {
      var MapPoint = window.require("esri/geometry/Point");
      var refPoint = (this._refPoint =
        this._refPoint || new MapPoint(0, 0, this.map.spatialReference));
      var geometry = evt.graphic.geometry;
      var point = geometry;

      if (geometry.type == "multipoint") {
        point = geometry.getPoint(evt.vertexinfo.pointIndex);
      } else if (geometry.type == "polygon") {
        point = geometry.getPoint(
          evt.vertexinfo.segmentIndex,
          evt.vertexinfo.pointIndex
        );
      } else if (geometry.type == "polyline") {
        point = geometry.getPoint(
          evt.vertexinfo.segmentIndex,
          evt.vertexinfo.pointIndex
        );
      }

      if (evt.transform) {
        refPoint.update(point.x + evt.transform.dx, point.y + evt.transform.dy);
      } else {
        refPoint.update(point.x, point.y);
      }

      this.currentPoint[1] = refPoint.getLatitude();
      this.currentPoint[0] = refPoint.getLongitude();
      this.pointView && this.pointView.render();
    },

    _onGraphicMove: function (evt) {
      var MapPoint = window.require("esri/geometry/Point");
      var refPoint = (this._refPoint =
        this._refPoint || new MapPoint(0, 0, this.map.spatialReference));
      var geometry = evt.graphic.geometry;
      var point = geometry;

      if (geometry.type == "multipoint") {
        point = {
          x: 0,
          y: 0,
        };
      } else if (geometry.type == "polygon") {
        point = geometry.getExtent().getCenter();
      } else if (geometry.type == "polyline") {
        point = geometry.getExtent().getCenter();
      }

      if (evt.transform) {
        refPoint.update(point.x + evt.transform.dx, point.y + evt.transform.dy);
      } else {
        refPoint.update(point.x, point.y);
      }

      this._graphicMoved = true;

      this.currentPoint[1] = refPoint.getLatitude();
      this.currentPoint[0] = refPoint.getLongitude();
      this.pointView && this.pointView.render();
    },

    _onGraphicMoveStop: function () {
      this.refreshGeometry();

      if (this._graphicMoved) {
        var contextMenu = [
          mapContext.getOperation("commit-geometry"),
          mapContext.getOperation("revert-location-geometry"),
        ];

        var geometryScreenCoords = GeoUtils.getScreenPoint(
          this.map,
          this._editingLabel
        );

        cms.displayContextMenu(
          geometryScreenCoords.x,
          geometryScreenCoords.y + this.$el.offset().top,
          contextMenu
        );
      }

      this._graphicMoved = false;

      this.getConfiguration().workingLayer.layer.refresh();
    },

    refresh: function () {
      var map = this.map;

      _.each(map.graphicsLayerIds, function (layerId) {
        var layer = map.getLayer(layerId);

        if (layer.visible) {
          layer.setVisibility(false);
          layer.setVisibility(true);
        }
      });
    },

    render: function () {
      var Map = window.require("esri/map");
      var Draw = window.require("esri/toolbars/draw");
      var Scalebar = window.require("esri/dijit/Scalebar");
      var LabelLayer = window.require("esri/layers/LabelLayer");

      this.$el.html(this.template(this.options));

      var $locationMap = this.$(".location-map");

      $locationMap.attr("id", $locationMap.attr("id") || _.uniqueId("map"));

      this.map = new Map($locationMap.attr("id"), {
        basemap: "streets", //this.getBaseMap(), - trying to set zoom level to something better
        center: this.getCenter(),
        zoom: 12,
        sliderPosition: "top-right",
        showAttribution: false,
        lods: [
          {
            level: 0,
            resolution: 156543.03392800014,
            scale: 591657527.591555,
          },
          {
            level: 1,
            resolution: 78271.51696399994,
            scale: 295828763.795777,
          },
          {
            level: 2,
            resolution: 39135.75848200009,
            scale: 147914381.897889,
          },
          {
            level: 3,
            resolution: 19567.87924099992,
            scale: 73957190.948944,
          },
          {
            level: 4,
            resolution: 9783.93962049996,
            scale: 36978595.474472,
          },
          {
            level: 5,
            resolution: 4891.96981024998,
            scale: 18489297.737236,
          },
          {
            level: 6,
            resolution: 2445.98490512499,
            scale: 9244648.868618,
          },
          {
            level: 7,
            resolution: 1222.992452562495,
            scale: 4622324.434309,
          },
          {
            level: 8,
            resolution: 611.4962262813797,
            scale: 2311162.217155,
          },
          {
            level: 9,
            resolution: 305.74811314055756,
            scale: 1155581.108577,
          },
          {
            level: 10,
            resolution: 152.87405657041106,
            scale: 577790.554289,
          },
          {
            level: 11,
            resolution: 76.43702828507324,
            scale: 288895.277144,
          },
          {
            level: 12,
            resolution: 38.21851414253662,
            scale: 144447.638572,
          },
          {
            level: 13,
            resolution: 19.10925707126831,
            scale: 72223.819286,
          },
          {
            level: 14,
            resolution: 9.554628535634155,
            scale: 36111.909643,
          },
          {
            level: 15,
            resolution: 4.77731426794937,
            scale: 18055.954822,
          },
          {
            level: 16,
            resolution: 2.388657133974685,
            scale: 9027.977411,
          },
          {
            level: 17,
            resolution: 1.1943285668550503,
            scale: 4513.988705,
          },
          {
            level: 18,
            resolution: 0.5971642835598172,
            scale: 2256.994353,
          },
          {
            level: 19,
            resolution: 0.29858214164761665,
            scale: 1128.497176,
          },
          {
            level: 20,
            resolution: 0.149291071,
            scale: 564.248588,
          },
          {
            level: 21,
            resolution: 0.074645535,
            scale: 282.124294,
          },
          {
            level: 22,
            resolution: 0.037322768,
            scale: 141.062147,
          },
          {
            level: 23,
            resolution: 0.018661384,
            scale: 70.5310735,
          },
          {
            level: 24,
            resolution: 0.009330692,
            scale: 35.26553675,
          },
        ],
      });

      this.map.on(
        "mouse-move",
        _.bind(function (evt) {
          this.currentPoint[1] = evt.mapPoint.getLatitude();
          this.currentPoint[0] = evt.mapPoint.getLongitude();
          this.pointView && this.pointView.render();
        }, this)
      );

      this.map.on(
        "zoom-start",
        _.bind(function (evt) {
          var that = this;
          setTimeout(function () {
            var map = that.map;
            _.each(map.graphicsLayerIds, function (layerId) {
              var layer = map.getLayer(layerId);
              if (layer.visible) {
                layer.originalVisibility = true;
                layer.setVisibility(false);
              }
            });
          }, 200);
        }, this)
      );

      this.map.on(
        "update-end",
        _.bind(function (evt) {
          var that = this;
          setTimeout(function () {
            var map = that.map;
            _.each(map.graphicsLayerIds, function (layerId) {
              var layer = map.getLayer(layerId);
              if (layer.originalVisibility) {
                delete layer.originalVisibility;
                layer.setVisibility(true);
              }
            });
          }, 200);
        }, this)
      );

      if (this.options.scalebar) {
        var scalebarOptions = {
          map: this.map,
          scalebarUnit: "dual",
        };

        if (_.isObject(this.options.scalebar)) {
          scalebarOptions = _.defaults(scalebarOptions, this.options.scalebar);
        }

        this._scalebar = new Scalebar(scalebarOptions);
      }

      this._labelLayer = new LabelLayer();
      this._labelLayer.setMinScale(18100); //should appear at zoom level 15
      this.map.addLayer(this._labelLayer);
      this.map.on("load", _.bind(this._mapLoad, this));

      this.draw = new Draw(this.map);
      this.draw.on(
        "draw-end",
        _.bind(function (evt) {
          if (!this.isMeasuring() && this.isDrawingActive() && this._drawMode) {
            this._drawingLayer.addGeometry(evt.geometry);
            this.deactivateEdit(true);
            this.deactivateDraw();
          }
        }, this)
      );

      var pointView = (this.pointView = new MapPointView({
        point: this.currentPoint,
      }));

      pointView.render();
      this.$(".location-map").after(pointView.$el);
    },

    /**
     * Resets the height of the map.
     * @param  {Number} height The new height value.
     */
    resetHeight: function (height) {
      _.defer(
        _.bind(function () {
          this.$el.add(this.$(".location-map")).css({
            height: height,
          });
          this.$(".location-map").css("width", "");
          this.map.resize(true);
          this.map.reposition();
        }, this)
      );
    },
  },
  {
    MERCATORXMIN: -19921840.43438468,
    MERCATORYMAX: 15436875.623637347,
    MERCATORXMAX: 19840090.183327414,
    MERCATORYMIN: -19589628.217752732,
  }
);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/MapView.js
// module id = 496
// module chunks = 1